"use client";

import { useEffect, useRef, useState } from "react";

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

function easeOutCubic(t: number): number {
  const u = clamp01(t);
  return 1 - (1 - u) ** 3;
}

/** Compact display for large integers (e.g. 1200 → "1.2k"). */
export function formatCompactNumber(n: number): string {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  const sign = x < 0 ? "-" : "";
  const ax = Math.abs(x);
  if (ax >= 1_000_000) {
    const v = ax / 1_000_000;
    const s = v >= 100 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, "");
    return sign + s + "M";
  }
  if (ax >= 1000) {
    const v = ax / 1000;
    const s = v >= 100 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, "");
    return sign + s + "k";
  }
  return sign + String(Math.round(ax));
}

/** Matches dashboard `formatCurrency` (Rand, en-ZA, two decimals). */
export function formatCurrencyCountUp(
  n: number,
  locale = "en-ZA",
  prefix = "R ",
): string {
  return prefix + Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export type CountUpMode = "integer" | "currency" | "compact";

export type UseCountUpOptions = {
  start: boolean;
  mode?: CountUpMode;
  locale?: string;
  currencyPrefix?: string;
  /** Bumps when the intro should fully reset (e.g. desk launch replay). */
  resetKey?: string | number;
};

/**
 * RAF-driven count-up with ease-out cubic. Returns "—" until `start` is true.
 * Final frame uses the live `target` so formatting matches settled data.
 */
export function useCountUp(target: number, durationMs: number, options: UseCountUpOptions): string {
  const { start, mode = "integer", locale = "en-ZA", currencyPrefix = "R ", resetKey = 0 } = options;
  const safeDuration = Math.max(1, durationMs);
  const [text, setText] = useState("—");
  const rafRef = useRef(0);

  useEffect(() => {
    if (!start) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      setText("—");
      return;
    }

    const end = Math.max(0, Number.isFinite(target) ? target : 0);

    const format = (v: number) => {
      const x = Math.max(0, Number.isFinite(v) ? v : 0);
      if (mode === "currency") return formatCurrencyCountUp(x, locale, currencyPrefix);
      if (mode === "compact") return formatCompactNumber(x);
      return String(Math.round(x));
    };

    let startTime: number | undefined;

    const tick = (now: number) => {
      if (startTime === undefined) startTime = now;
      const elapsed = now - startTime;
      const u = Math.min(1, elapsed / safeDuration);
      const eased = easeOutCubic(u);
      const cur = end * eased;
      if (u >= 1) {
        setText(format(end));
        return;
      }
      setText(format(cur));
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [start, safeDuration, mode, locale, currencyPrefix, resetKey, target]);

  return text;
}
